import { zeroAddress } from 'viem'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import { BPS, parseRawAmount } from '../../../utils/parsing'
import { AdditionalYields, GenericTokenList } from '../../../types'
import { createMarketUid } from '../../../utils'
import { FluidApiVaultEnrichment, getCachedFluidApiData } from './apiData'
import { FluidDexStateMap, getCachedFluidDexState } from './dexState'

// Fluid uses the EEE sentinel (`0xEeee…EEeE`) on-chain for native ETH. The
// rest of the lending stack normalizes native to `zeroAddress` — see
// `isNativeAddress` / `normalizeNativeAddress` in calldata-sdk. We coerce
// here at the parser boundary so downstream code (token list lookups,
// market UIDs, price keys, calldata builders) only ever sees zeroAddress
// for native.
const FLUID_EEE_LOWER = '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee'
const normalizeUnderlying = (addr: string): string =>
  addr === FLUID_EEE_LOWER ? zeroAddress : addr

/**
 * Fluid-specific:
 *   - Rates in resolver are 1e2 precision (1% = 100, 100% = 10000).
 *   - APR not APY — no conversion needed beyond scaling.
 */
const FLUID_RATE_SCALE = 1e2

const scaleFluidRate = (raw: string | number | bigint | undefined): number => {
  if (raw === undefined || raw === null) return 0
  return Number(raw) / FLUID_RATE_SCALE
}

/**
 * Per-token Liquidity-layer state relevant for the IRM.
 *
 * `utilization` is `lastStoredUtilization` scaled to a 0-1 fraction. It is
 * read from the same packed storage slot as the current borrow rate, so it is
 * the exact x-coordinate on the token's rate curve that produced the rate —
 * the canonical "current utilization" for IRM charts and simulations.
 */
export interface FluidLiquidityTokenState {
  /** Liquidity-layer utilization (0-1 fraction; the IRM input) */
  utilization: number
  /** Liquidity revenue fee (0-1 fraction of borrow interest) */
  fee: number
  /** Utilization cap (0-1 fraction; 1 when unset) */
  maxUtilization: number
  /** Total token supply at the Liquidity layer (raw token units, string) */
  totalSupply: string
  /** Total token borrow at the Liquidity layer (raw token units, string) */
  totalBorrow: string
  /** Liquidity-layer borrow APR % */
  borrowRate: number
  /** Liquidity-layer supply APR % */
  supplyRate: number
}

/**
 * Map OverallTokenData[] (LiquidityResolver.getAllOverallTokensData) to a
 * token-address-keyed lookup. The token address lives inside the active rate
 * version's sub-struct; the inactive version's `token` is the zero address.
 */
const parseOverallTokensData = (
  overallTokensData: any,
): { [token: string]: FluidLiquidityTokenState } => {
  const out: { [token: string]: FluidLiquidityTokenState } = {}
  if (!Array.isArray(overallTokensData)) return out

  for (const td of overallTokensData) {
    const rateData = td?.rateData
    if (!rateData) continue
    const version = Number(rateData.version ?? 1)
    const rawToken = (
      (version === 2
        ? rateData.rateDataV2?.token
        : rateData.rateDataV1?.token) ?? ''
    )
      .toString()
      .toLowerCase()
    // A real zero address marks an inactive/empty row; Fluid's native-ETH
    // sentinel is non-zero here and only maps to zeroAddress via normalize.
    if (!rawToken || rawToken === zeroAddress) continue
    const token = normalizeUnderlying(rawToken)

    const maxUtilizationRaw = Number(td.maxUtilization ?? 0)
    out[token] = {
      utilization: Number(td.lastStoredUtilization ?? 0) / BPS,
      fee: Number(td.fee ?? 0) / BPS,
      maxUtilization: maxUtilizationRaw > 0 ? maxUtilizationRaw / BPS : 1,
      totalSupply: (td.totalSupply ?? 0n).toString(),
      totalBorrow: (td.totalBorrow ?? 0n).toString(),
      borrowRate: scaleFluidRate(td.borrowRate),
      supplyRate: scaleFluidRate(td.supplyRate),
    }
  }

  return out
}

/**
 * Parser for the two-call Fluid public data fetch built by `buildFluidCall`.
 *
 * data[0] = VaultEntireData[]   (VaultResolver.getVaultsEntireData)
 * data[1] = OverallTokenData[]  (LiquidityResolver.getAllOverallTokensData)
 *
 * Emits one bucket per vault under lender key `FLUID_<chainId>_<vaultId>` (mirrors the
 * Morpho Blue pattern of one lender per isolated market).
 *
 * fTokens are NOT handled here — they are standalone ERC-4626 vaults on the
 * shared Liquidity Layer, not a lending market, so they are out of scope.
 */
export const getFluidPublicDataConverter = (
  _lender: string,
  chainId: string,
  prices: { [asset: string]: number },
  additionalYields: AdditionalYields,
  tokenList: GenericTokenList = {},
): [(data: any[]) => { [lender: string]: any } | undefined, number] => {
  const expectedNumberOfCalls = 2

  return [
    (data: any[]) => {
      if (data.length !== expectedNumberOfCalls) return undefined

      const [vaultEntireData, overallTokensData] = data
      // Degrades to an empty map when the Liquidity call failed ('0x') —
      // affected rows fall back to utilization 0 rather than dropping the vault.
      const liquidityByToken = parseOverallTokensData(overallTokensData)
      // Smart (T2/T3/T4) vaults need two things a vault-resolver call cannot
      // give: the LP share -> token conversion (on-chain, from the DEX
      // resolver — complete coverage) and the DEX trading yield (Fluid API
      // only — it is published nowhere on-chain). Both are primed before
      // conversion because this converter is synchronous. Without the share
      // conversion a smart vault is skipped rather than mis-sized.
      const dexState = getCachedFluidDexState(chainId)
      const apiData = getCachedFluidApiData(chainId)
      const out: { [lender: string]: any } = {}

      for (const vault of vaultEntireData ?? []) {
        const parsed = parseVault(
          vault,
          chainId,
          prices,
          additionalYields,
          tokenList,
          liquidityByToken,
          apiData?.[Number(vault?.constantVariables?.vaultId ?? -1)],
          dexState,
        )
        if (parsed) {
          out[parsed.lender] = {
            data: parsed.data,
            params: parsed.params,
            chainId,
          }
        }
      }

      return out
    },
    expectedNumberOfCalls,
  ]
}

// --------------------------------------------------------------------
// Vault (borrow position, isolated per vault)
// --------------------------------------------------------------------

/**
 * The DEX trading yield on a SMART DEBT side OFFSETS the borrow cost.
 *
 * Fluid's "smart debt" product is exactly this: the borrowed position doubles
 * as DEX liquidity, so the trading fees it earns are netted against interest.
 * The API reports the magnitude as a positive number (`borrowRate.dex.trading`,
 * equal to the `/dexes` route's `apy.borrowApy`); the sign is ours to apply.
 *
 * A smart-debt leg can therefore legitimately print a NEGATIVE borrow rate when
 * trading yield exceeds the liquidity-layer interest. That is a real
 * phenomenon, not an error, so it is deliberately NOT clamped to zero —
 * clamping would hide the entire reason a user picks a smart-debt vault.
 * (dTRINITY's rebate-driven negative net borrow rate is the existing precedent
 * for a negative borrow rate surviving to the API.)
 */
const SMART_DEBT_TRADING_SIGN = -1

/** One underlying leg of a vault side, already converted to token units. */
interface SideLeg {
  /** Normalized underlying address (`zeroAddress` for native). */
  token: string
  decimals: number
  /** Total on this leg, token units. */
  amount: number
  /** Withdrawable (collateral) or borrowable (debt) headroom, token units. */
  available: number
  /** Nominal APR in percent for this leg on this side. */
  rate: number
}

/**
 * A smart side's share conversion, normalized across its two possible sources
 * (the on-chain DEX resolver, preferred; the Fluid API, fallback).
 */
interface SideShares {
  /** The Fluid DEX pool backing this side. */
  dex: string
  /** Token units per 1e18 LP shares, parallel to the side's token0/token1. */
  perShare: [bigint, bigint]
  /** Decimals of each leg token — NEVER the vault's 18-decimal share scale. */
  decimals: [number, number]
}

/**
 * Split a side's raw total into per-leg token amounts.
 *
 * Simple side  → one leg, the raw total in the side's own decimals.
 * Smart side   → two legs; the raw total is a count of 1e18 DEX LP SHARES and
 *                each leg is `shares * tokenXPerShare / 1e18`, where
 *                `tokenXPerShare` is already in that token's OWN decimals.
 */
const splitSide = (
  tokens: string[],
  rawTotal: bigint,
  rawAvailable: bigint,
  simpleDecimals: number,
  shares: SideShares | undefined,
  rateFor: (token: string, index: number) => number,
): SideLeg[] => {
  if (!shares) {
    const token = tokens[0]
    if (!token) return []
    return [
      {
        token,
        decimals: simpleDecimals,
        amount: Number(parseRawAmount(rawTotal.toString(), simpleDecimals)),
        available: Number(
          parseRawAmount(rawAvailable.toString(), simpleDecimals),
        ),
        rate: rateFor(token, 0),
      },
    ]
  }

  const perShare = shares.perShare
  const decimals = shares.decimals
  const legs: SideLeg[] = []
  for (let i = 0; i < tokens.length && i < 2; i++) {
    const token = tokens[i]
    if (!token) continue
    // Share math in bigint before the lossy Number conversion.
    const amountRaw = (rawTotal * perShare[i]) / 10n ** 18n
    const availableRaw = (rawAvailable * perShare[i]) / 10n ** 18n
    legs.push({
      token,
      decimals: decimals[i],
      amount: Number(parseRawAmount(amountRaw.toString(), decimals[i])),
      available: Number(parseRawAmount(availableRaw.toString(), decimals[i])),
      rate: rateFor(token, i),
    })
  }
  return legs
}

function parseVault(
  vault: any,
  chainId: string,
  prices: { [asset: string]: number },
  additionalYields: AdditionalYields,
  tokenList: GenericTokenList,
  liquidityByToken: { [token: string]: FluidLiquidityTokenState } = {},
  api?: FluidApiVaultEnrichment,
  dexState?: FluidDexStateMap,
):
  | {
      lender: string
      data: any
      params: any
    }
  | undefined {
  const vaultAddress = (vault.vault ?? '').toLowerCase()
  const constants = vault.constantVariables
  const configs = vault.configs
  const rates = vault.exchangePricesAndRates
  const totals = vault.totalSupplyAndBorrow
  const limits = vault.limitsAndAvailability
  if (!vaultAddress || !constants || !configs || !rates || !totals) {
    return undefined
  }

  const isSmartCol = !!vault.isSmartCol
  const isSmartDebt = !!vault.isSmartDebt

  /**
   * A side's underlying legs.
   *
   * `isSmart` — NOT the value of `token1` — decides whether the side has two
   * legs. Fluid's native-ETH sentinel normalizes to `zeroAddress`, which is
   * also what an ABSENT `token1` decodes to, so testing `token1 !== zero` would
   * silently drop the native leg of every ETH-paired DEX (wstETH/ETH, USDC/ETH
   * — including vault 44, the single largest smart market).
   */
  const sideTokens = (side: any, isSmart: boolean): string[] => {
    const raw = side?.token0 !== undefined ? side : { token0: side }
    const t0 = normalizeUnderlying((raw?.token0 ?? '').toString().toLowerCase())
    if (!isSmart) return [t0]
    const t1 = normalizeUnderlying((raw?.token1 ?? '').toString().toLowerCase())
    return [t0, t1]
  }

  const colTokens = sideTokens(constants.supplyToken, isSmartCol)
  const debtTokens = sideTokens(constants.borrowToken, isSmartDebt)

  if (!colTokens[0] || !debtTokens[0]) return undefined
  // A smart side must actually expose both legs, or the share split is wrong.
  if (isSmartCol && colTokens.length < 2) return undefined
  if (isSmartDebt && debtTokens.length < 2) return undefined

  const vaultId = Number(constants.vaultId ?? 0)
  const vaultType = Number(constants.vaultType ?? 0)
  const lender = `FLUID_${chainId}_${vaultId}`

  /**
   * Decimals for a SIMPLE side — the token list is the authority.
   *
   * `constantVariables.supplyDecimals` / `.borrowDecimals` DO NOT EXIST on the
   * VaultResolver ABI, so that lookup has always been dead — the pre-smart code
   * read `constants.supplyDecimals ?? tokenList[...]?.decimals ?? 18` and the
   * TOKEN LIST fallback was silently carrying it. Production was correct; this
   * is not a repair.
   *
   * What is new is that decimals are resolved PER LEG. One value per side
   * cannot describe a smart side whose legs differ (sUSDai 18 + USDC 6), so the
   * token list is consulted first and per token. The resolver lookup is kept
   * only so a future resolver that does publish the field is picked up.
   *
   * A smart vault gets no 18 fallback: its legs are LP tokens where a wrong
   * guess is catastrophic rather than merely wrong, so an unknown token drops
   * the vault instead. T1 keeps the 18 last resort so nothing that served
   * before stops serving.
   */
  const isSmartVault = isSmartCol || isSmartDebt
  const simpleDecimals = (
    token: string,
    resolverValue: any,
  ): number | undefined => {
    const listed = tokenList[token]?.decimals
    if (listed !== undefined && listed !== null) return Number(listed)
    if (resolverValue !== undefined && resolverValue !== null)
      return Number(resolverValue)
    return isSmartVault ? undefined : 18
  }

  const colDecimals = simpleDecimals(colTokens[0], constants.supplyDecimals)
  const debtDecimals = simpleDecimals(debtTokens[0], constants.borrowDecimals)
  // A simple side with unknown decimals cannot be sized; drop rather than guess.
  if (
    (!isSmartCol && colDecimals === undefined) ||
    (!isSmartDebt && debtDecimals === undefined)
  ) {
    return undefined
  }

  /**
   * Resolve a smart side's share conversion.
   *
   * `constantVariables.supply` / `.borrow` IS the DEX pool address on a smart
   * side (verified against the API on all 43 listed Ethereum smart vaults —
   * 43/43 exact), so no extra lookup is needed to find the pool.
   *
   * On-chain DEX state is preferred because it covers every deployed pool; the
   * API fallback exists only for an RPC failure and covers listed vaults only.
   * Leg decimals must be a real token's decimals — the vault's own
   * supply/borrow decimals describe the 18-decimal LP SHARE on a smart side,
   * so using them would misprice any 6-decimal leg by 1e12.
   */
  const resolveShares = (
    isSmart: boolean,
    sideContract: any,
    tokens: string[],
    onChainPerShare: (s: any) => [bigint, bigint],
    apiSide:
      | {
          token0PerShare: bigint
          token1PerShare: bigint
          token0Decimals: number
          token1Decimals: number
        }
      | undefined,
  ): SideShares | undefined => {
    if (!isSmart) return undefined
    const dex = String(sideContract ?? '').toLowerCase()
    const decimalsFor = (
      token: string,
      fallback?: number,
    ): number | undefined => {
      const d = tokenList[token]?.decimals
      if (d !== undefined && d !== null) return Number(d)
      return fallback
    }
    const onChain = dex ? dexState?.[dex] : undefined
    const perShare = onChain
      ? onChainPerShare(onChain)
      : apiSide
        ? ([apiSide.token0PerShare, apiSide.token1PerShare] as [bigint, bigint])
        : undefined
    if (!perShare) return undefined
    const d0 = decimalsFor(tokens[0], apiSide?.token0Decimals)
    const d1 = decimalsFor(tokens[1], apiSide?.token1Decimals)
    // Guessing 18 here is exactly the bug that would understate a USDC leg by
    // 1e12, so an unknown leg decimal drops the vault instead.
    if (d0 === undefined || d1 === undefined) return undefined
    return { dex, perShare, decimals: [d0, d1] }
  }

  const colShares = resolveShares(
    isSmartCol,
    constants.supply,
    colTokens,
    (s) => [s.token0PerSupplyShare, s.token1PerSupplyShare],
    api?.supplyShares,
  )
  const debtShares = resolveShares(
    isSmartDebt,
    constants.borrow,
    debtTokens,
    (s) => [s.token0PerBorrowShare, s.token1PerBorrowShare],
    api?.borrowShares,
  )
  // A smart side whose share conversion is unavailable cannot be sized at all:
  // its totals would be read as token amounts when they are share counts. Skip
  // the vault rather than publish a wrong number.
  if ((isSmartCol && !colShares) || (isSmartDebt && !debtShares)) {
    return undefined
  }

  const supplyRate = scaleFluidRate(rates.supplyRateVault)
  const borrowRate = scaleFluidRate(rates.borrowRateVault)
  const supplyDexTrading = (api?.supplyDexTradingRate ?? 0) / FLUID_RATE_SCALE
  const borrowDexTrading = (api?.borrowDexTradingRate ?? 0) / FLUID_RATE_SCALE

  const colLegs = splitSide(
    colTokens,
    BigInt(totals.totalSupplyVault?.toString() ?? '0'),
    BigInt(limits?.withdrawable?.toString() ?? '0'),
    colDecimals ?? 18,
    colShares,
    (token) =>
      // Simple side: the vault's own supply rate is authoritative on-chain.
      // Smart side: that field is 0, so the rate is the token's own
      // Liquidity-layer supply rate plus the DEX trading yield the LP earns.
      isSmartCol
        ? (liquidityByToken[token]?.supplyRate ?? 0) + supplyDexTrading
        : supplyRate,
  )
  const debtLegs = splitSide(
    debtTokens,
    BigInt(totals.totalBorrowVault?.toString() ?? '0'),
    BigInt(limits?.borrowable?.toString() ?? '0'),
    debtDecimals ?? 18,
    debtShares,
    (token) =>
      isSmartDebt
        ? (liquidityByToken[token]?.borrowRate ?? 0) +
          SMART_DEBT_TRADING_SIGN * borrowDexTrading
        : borrowRate,
  )

  if (!colLegs.length || !debtLegs.length) return undefined

  // Per-collateral lock-up utilization at the Liquidity Layer.
  // Each vault is its own LL user, so `withdrawLimit` is the floor below
  // which this vault's collateral cannot drop right now. Ratio is computed
  // in raw bigint units to preserve precision before lossy Number conversion.
  // Both operands are in the SAME unit (tokens on a simple side, LP shares on
  // a smart one), so the ratio is unit-free and holds for either shape.
  const totalSupplyRaw = BigInt(totals.totalSupplyVault?.toString() ?? '0')
  const withdrawLimitRaw = BigInt(limits?.withdrawLimit?.toString() ?? '0')
  const lockupRatio =
    totalSupplyRaw > 0n
      ? Number((withdrawLimitRaw * 10000n) / totalSupplyRaw) / 10000
      : 0

  // Fluid config values are 1e4 precision (basis points).
  const ltv = Number(configs.collateralFactor ?? 0) / BPS
  const liquidationThreshold = Number(configs.liquidationThreshold ?? 0) / BPS
  const liquidationPenalty = Number(configs.liquidationPenalty ?? 0) / BPS

  const priceKeyFor = (token: string) => {
    const meta = tokenList[token]
    return toOracleKey(meta?.assetGroup) ?? toGenericPriceKey(token, chainId)
  }

  /**
   * Value-weighted rate across a smart side's legs — the number that describes
   * the position. Weights are each leg's USD value within THIS vault, i.e. the
   * pool composition; a side with no value falls back to its first leg's rate
   * so an empty vault reports something rather than NaN.
   */
  const basketRate = (legs: SideLeg[]): number => {
    let value = 0
    let weighted = 0
    for (const leg of legs) {
      const v = leg.amount * (prices[priceKeyFor(leg.token)] ?? 0)
      if (v <= 0) continue
      value += v
      weighted += v * leg.rate
    }
    return value > 0 ? weighted / value : (legs[0]?.rate ?? 0)
  }
  /**
   * Tokens that are a leg of a SMART side — the only rows `autoBalanced` may
   * describe.
   *
   * It used to be stamped on every row of a smart vault (`isSmartVault`), which
   * is wrong for the side that is NOT smart: a T2's debt token is an ordinary
   * single-asset loan, and vault #74 (weETH+ETH collateral / wstETH debt) was
   * telling anyone who read the wstETH row that supplying or borrowing wstETH
   * meant holding a rebalancing basket. It does not. Depositing a market's loan
   * token is a plain deposit even when its COLLATERAL is an LP.
   */
  const smartLegTokens = new Set<string>([
    ...(isSmartCol ? colLegs.map((l) => l.token.toLowerCase()) : []),
    ...(isSmartDebt ? debtLegs.map((l) => l.token.toLowerCase()) : []),
  ])

  const basketSupplyRate = isSmartCol ? basketRate(colLegs) : undefined
  const basketBorrowRate = isSmartDebt ? basketRate(debtLegs) : undefined

  // Totals in the IRM's basis (the LL token pool) for rate-impact math.
  const irmTotals = (
    state: FluidLiquidityTokenState | undefined,
    decimals: number,
  ) =>
    state
      ? {
          irmTotalDeposits: Number(parseRawAmount(state.totalSupply, decimals)),
          irmTotalDebt: Number(parseRawAmount(state.totalBorrow, decimals)),
        }
      : {}

  // ------------------------------------------------------------------
  // Rows, keyed by underlying.
  //
  // A vault side contributes one row PER LEG (the Gearbox V3 shape: N sibling
  // rows under one lender key, never one row carrying two assets — every
  // consumer, from `PoolData.asset` to the health math in
  // `createMultiAccountTypeUserState`, is single-asset).
  //
  // A token can appear on BOTH sides: 4 of 11 Ethereum T4 vaults use the SAME
  // DEX for collateral and debt (e.g. wstETH+ETH against wstETH+ETH), so the
  // legs collide by address. They MERGE into one row that is both collateral
  // and debt rather than overwriting each other.
  // ------------------------------------------------------------------
  const data: any = {}
  const ensureRow = (leg: SideLeg) => {
    const marketUid = createMarketUid(chainId, lender, leg.token)
    if (data[marketUid]) return data[marketUid]

    const meta = tokenList[leg.token]
    const priceKey = priceKeyFor(leg.token)
    const liquidity = liquidityByToken[leg.token]
    const row = {
      marketUid,
      name: '',
      poolId: leg.token,
      underlying: leg.token,
      asset: meta,
      totalDeposits: 0,
      totalDebtStable: 0,
      totalDebt: 0,
      totalLiquidity: 0,
      borrowLiquidity: 0,
      totalDepositsUSD: 0,
      totalDebtStableUSD: 0,
      totalDebtUSD: 0,
      totalLiquidityUSD: 0,
      borrowLiquidityUSD: 0,
      // IRM x-coordinate: this token's Liquidity-layer utilization.
      utilization: liquidity?.utilization ?? 0,
      ...irmTotals(liquidity, leg.decimals),
      lockupRatio,
      depositRate: 0,
      variableBorrowRate: 0,
      stableBorrowRate: 0,
      intrinsicYield: additionalYields?.intrinsicYields?.[priceKey] ?? 0,
      rewards: [] as any[],
      decimals: leg.decimals,
      config: {
        '0': {
          category: 0,
          borrowCollateralFactor: 0,
          collateralFactor: 0,
          borrowFactor: 1,
          liquidationPenalty: 0,
          closeFactor: 1,
          collateralDisabled: true,
          debtDisabled: true,
        },
      },
      liquidationBonus: 0,
      closeFactor: 1,
      collateralActive: false,
      borrowingEnabled: false,
      depositsEnabled: false,
      hasStable: false,
      isActive: true,
      isFrozen: false,
      /**
       * Smart-vault descriptor, read by the Fluid term adapter.
       *
       * Present ONLY on a T2/T3/T4 row, where this leg is one half of a
       * two-token DEX LP position rather than a plain deposit or debt. Without
       * it a smart market is described as an ordinary pool — the generic
       * builder cannot tell them apart.
       */
      /**
       * THIS ROW'S ASSET IS NOT INDEPENDENTLY HOLDABLE.
       *
       * LENDER-AGNOSTIC ON PURPOSE — it sits on the ROW, not inside `fluid`,
       * because the shape is not Fluid's alone: Lista SmartLP's collateral
       * receipt, GMX's GM/GLV baskets and Fluid's smart sides are all market
       * sides whose position unit is a multi-token basket (the same set
       * LP_ACTIONS_PLAN.md enumerates). Any of them can set this, and a
       * consumer branches on it once instead of learning each protocol.
       *
       * True means: depositing any leg mints a share of a basket over ALL of
       * them, and the POOL — not the user — sets and continuously re-sets the
       * ratio. There is no way to "hold the ETH leg" of a USDC+ETH side; the
       * split drifts with every trade and with the range shifting around its
       * centre price.
       *
       * Three consequences a consumer must handle: "you deposited X" is false
       * from the first block; a single leg's rate is right per DOLLAR but is
       * NOT the position's rate (use the basket rate); and an exit has to be
       * sized in shares rather than in this token.
       */
      ...(smartLegTokens.has(leg.token.toLowerCase())
        ? { autoBalanced: true }
        : {}),
      fluid: isSmartVault
        ? {
            vaultType,
            /**
             * The rate of the POSITION, not of this leg.
             *
             * A per-leg rate is individually correct — every dollar in the LP
             * earns the DEX trading yield regardless of which token it sits in,
             * so `legRate = liquidityRate + tradingRate` is right per dollar.
             * What it is NOT is the vault's APR: ranking or headlining a single
             * leg overstates the position whenever the legs differ. On the live
             * USDC+ETH/USDC+ETH vault the legs read 11.81 % and 8.19 % while
             * the position earns 10.33 % — and a "best APR" taken as the max
             * over legs shows 11.81 %.
             *
             * Value-weighted by each leg's own share of the side, which is the
             * pool's composition as this vault holds it. Fluid's own UI
             * publishes exactly this figure (10.33 % supply / −1.72 % borrow),
             * and these match it to the basis point.
             */
            basketSupplyRate,
            basketBorrowRate,
            isSmartCol,
            isSmartDebt,
            /** Both legs of the collateral LP, in token0/token1 order. */
            collateralPair: isSmartCol ? colTokens : undefined,
            /** Both legs of the debt LP, in token0/token1 order. */
            debtPair: isSmartDebt ? debtTokens : undefined,
            /** Percent APR the LP earns; supply ADDS it, debt NETS it off. */
            supplyDexTradingRate: supplyDexTrading,
            borrowDexTradingRate: borrowDexTrading,
          }
        : undefined,
      // Price cached for the second pass; stripped before returning.
      __price: prices[priceKey] ?? 0,
    }
    data[marketUid] = row
    return row
  }

  for (const leg of colLegs) {
    const row = ensureRow(leg)
    const price = row.__price
    row.totalDeposits += leg.amount
    row.totalLiquidity += leg.amount
    row.totalDepositsUSD += leg.amount * price
    row.totalLiquidityUSD += leg.amount * price
    row.depositRate = leg.rate
    row.collateralActive = true
    row.depositsEnabled = true
    row.liquidationBonus = liquidationPenalty
    row.config['0'].borrowCollateralFactor = ltv
    row.config['0'].collateralFactor = liquidationThreshold
    row.config['0'].liquidationPenalty = liquidationPenalty
    row.config['0'].collateralDisabled = false
    row.name = 'Collateral ' + (row.asset?.symbol ?? '')
  }

  for (const leg of debtLegs) {
    const row = ensureRow(leg)
    const price = row.__price
    row.totalDebt += leg.amount
    row.totalLiquidity += leg.available
    row.borrowLiquidity += leg.available
    row.totalDebtUSD += leg.amount * price
    row.totalLiquidityUSD += leg.available * price
    row.borrowLiquidityUSD += leg.available * price
    row.variableBorrowRate = leg.rate
    row.borrowingEnabled = true
    row.config['0'].debtDisabled = false
    // A merged row (same token is collateral AND debt — the same-DEX T4 shape)
    // keeps its collateral name; the row is genuinely both.
    if (!row.collateralActive) row.name = 'Loan ' + (row.asset?.symbol ?? '')
  }

  for (const row of Object.values<any>(data)) delete row.__price

  // Liquidity-Layer per-vault supply state — drives the dynamic withdrawal
  // limit. `expandPercent` is bps (1e4), `expandDuration` is seconds.
  // Consumers can project a future `withdrawalLimit` by linearly expanding
  // from `lastUpdateTimestamp` toward `baseWithdrawalLimit`.
  const llSupply = vault.liquidityUserSupplyData
  const liquidityUserSupply = llSupply
    ? {
        withdrawable: colLegs[0]?.available ?? 0,
        withdrawalLimit: llSupply.withdrawalLimit?.toString() ?? '0',
        baseWithdrawalLimit: llSupply.baseWithdrawalLimit?.toString() ?? '0',
        expandPercent: Number(llSupply.expandPercent ?? 0),
        expandDuration: Number(llSupply.expandDuration ?? 0),
        lastUpdateTimestamp: Number(llSupply.lastUpdateTimestamp ?? 0),
      }
    : undefined

  // Liquidity-Layer token state per vault side — the exact IRM inputs.
  // Totals are formatted to token units so rate-impact simulations can shift
  // utilization by (Δ / totalSupply) at the layer where rates are set.
  const formatLiquidity = (
    state: FluidLiquidityTokenState | undefined,
    decimals: number,
  ) =>
    state
      ? {
          utilization: state.utilization,
          fee: state.fee,
          maxUtilization: state.maxUtilization,
          totalSupply: Number(parseRawAmount(state.totalSupply, decimals)),
          totalBorrow: Number(parseRawAmount(state.totalBorrow, decimals)),
          borrowRate: state.borrowRate,
          supplyRate: state.supplyRate,
        }
      : undefined

  const sideDescriptor = (legs: SideLeg[], shares: SideShares | undefined) => ({
    /** Underlying legs in the vault's own token0/token1 order. */
    assets: legs.map((l) => ({ underlying: l.token, decimals: l.decimals })),
    /** Fluid DEX pool backing this side; `null` when the side is simple. */
    dex: shares?.dex ?? null,
    /** Token units per 1e18 LP shares, parallel to `assets`. `null` when simple. */
    perShare: shares
      ? [shares.perShare[0].toString(), shares.perShare[1].toString()]
      : null,
  })

  return {
    lender,
    data,
    params: {
      vault: {
        lender,
        vaultAddress,
        vaultId,
        /** Raw `TYPE()` — 10000/20000/30000/40000 for T1/T2/T3/T4. */
        vaultType,
        // Kept for backwards compatibility: the FIRST leg of each side. On a
        // smart side this is token0 only — read `collateral` / `loan` below
        // for the full picture, and never assume a single asset per side.
        collateralAddress: colTokens[0],
        loanAddress: debtTokens[0],
        collateralDecimals: colLegs[0]?.decimals ?? colDecimals ?? 18,
        loanDecimals: debtLegs[0]?.decimals ?? debtDecimals ?? 18,
        /** Full per-side descriptor incl. the DEX and its share conversion. */
        collateral: sideDescriptor(colLegs, colShares),
        loan: sideDescriptor(debtLegs, debtShares),
        oracle: configs.oracle,
        rebalancer: configs.rebalancer,
        liquidationThreshold,
        ltv,
        liquidationPenalty,
        oraclePriceOperate: configs.oraclePriceOperate?.toString(),
        oraclePriceLiquidate: configs.oraclePriceLiquidate?.toString(),
        isSmartCol,
        isSmartDebt,
        /** DEX trading yield, percent APR. Supply ADDS it, debt NETS it off. */
        supplyDexTradingRate: supplyDexTrading,
        borrowDexTradingRate: borrowDexTrading,
        liquidityUserSupply,
        liquidity: {
          supplyToken: formatLiquidity(
            liquidityByToken[colTokens[0]],
            colLegs[0]?.decimals ?? colDecimals ?? 18,
          ),
          borrowToken: formatLiquidity(
            liquidityByToken[debtTokens[0]],
            debtLegs[0]?.decimals ?? debtDecimals ?? 18,
          ),
        },
      },
    },
  }
}
